import { Location } from '@angular/common';
import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, UrlTree } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { ApplyRouteSeoUseCase } from '@ui/features/route-config/application/use-cases/apply-route-seo.use-case';
import { ApplyRouteLocaleUrlUseCase } from '@ui/features/route-config/application/use-cases/apply-route-locale-url.use-case';
import { readLocalePolicy } from '@ui/features/locale/application/services/read-locale-policy';
import { LocaleResolutionPolicy } from '@app/common/locale/domain/entities/locale-resolution-policy';
import { AngularI18nService } from 'common/data/i18n/services/angular-i18n.service';
import { SourceService } from '@app/core/services/source.service';
import { ReferralsService } from '@app/core/services/referrals.service';
import { PublicLocaleStorageService } from '@app/common/locale/application/services/public-locale-storage.service';
import { UrlUpdateService } from '@app/core/services/url-update.service';
import {
    extractLocalePrefixFromPath,
    isSeoLocale,
    normalizeLocaleToken,
    normalizeSeoLocale,
    SEO_DEFAULT_LOCALE,
    SeoLocale,
} from '@ui/features/route-config/domain/entities/seo-locale';
import { toPathOnly } from '@ui/features/route-config/domain/helpers/current-path.helper';

interface UrlNormalizationInput {
    readonly locale: SeoLocale;
    readonly urlLocaleContext: string | null;
}

interface ResolvedLocaleContext {
    readonly currentPath: string;
    readonly resolvedLocale: SeoLocale;
}

/**
 * Single source of truth for NavigationEnd handling (SEO, referrals, URL normalization).
 * El idioma NO se resuelve aca: lo aplica resolvePublicLocaleGuard antes de activar la ruta
 * (LocaleResolutionService). Aca solo se reconcilia el segmento de URL/pais de las rutas
 * UrlWithStorage; UrlOnly y UserOnly no leen storage ni reescriben la URL por idioma.
 *
 * Route locale + SEO decisions are sourced from route-config as the canonical layer.
 *
 * providedIn: 'root' — initialized once via APP_INITIALIZER in AppModule.
 */
@Injectable({ providedIn: 'root' })
export class RouteBootstrapService implements OnDestroy {
    private subscription: Subscription | null = null;

    public constructor(
        private readonly router: Router,
        private readonly location: Location,
        private readonly applyRouteSeoUseCase: ApplyRouteSeoUseCase,
        private readonly applyRouteLocaleUrlUseCase: ApplyRouteLocaleUrlUseCase,
        private readonly angularI18nService: AngularI18nService,
        private readonly sourceService: SourceService,
        private readonly referralsService: ReferralsService,
        private readonly publicLocaleStorageService: PublicLocaleStorageService,
        private readonly urlUpdateService: UrlUpdateService,
    ) {}

    /** Called once from APP_INITIALIZER. */
    public initialize(): void {
        this.subscription = this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                switchMap((event: NavigationEnd): Observable<ResolvedLocaleContext> => {
                    const currentUrlTree: UrlTree = this.router.parseUrl(event.urlAfterRedirects);

                    this.sourceService.listenParams(currentUrlTree.queryParams);

                    return this.applyRouteContext(
                        event.urlAfterRedirects,
                        readLocalePolicy(this.router.routerState.snapshot.root),
                    );
                }),
            )
            .subscribe((context: ResolvedLocaleContext): void => {
                this.applyRouteSeoUseCase.execute(
                    context.currentPath,
                    context.resolvedLocale,
                    this.router.routerState.snapshot.root,
                );
            });
    }

    private applyRouteContext(
        rawUrl: string,
        policy: LocaleResolutionPolicy,
    ): Observable<ResolvedLocaleContext> {
        const cleanUrl: string = this.referralsService.extractReferralIdFromUrlParams(rawUrl);

        if (cleanUrl !== rawUrl) {
            this.location.replaceState(cleanUrl);
        }

        const currentPath: string = toPathOnly(cleanUrl);
        const currentLocale: SeoLocale = this.extractLocaleFromPath(currentPath);
        const context$: Observable<ResolvedLocaleContext> =
            policy === LocaleResolutionPolicy.UrlWithStorage
                ? this.normalizeLocaleUrl(cleanUrl, currentPath, currentLocale)
                : of({ currentPath, resolvedLocale: currentLocale });

        return context$.pipe(
            tap((): void => {
                this.angularI18nService.loadSection('landing');
                // replaceState() no dispara NavigationEnd ni actualiza Router.url, por eso se
                // notifica el path final explicitamente para que currentPath$ tenga la URL real.
                this.urlUpdateService.notifyPathChanged(this.location.path());
            }),
        );
    }

    private normalizeLocaleUrl(
        cleanUrl: string,
        currentPath: string,
        currentLocale: SeoLocale,
    ): Observable<ResolvedLocaleContext> {
        const localeContextFromUrl: string | null = extractLocalePrefixFromPath(currentPath);

        return this.resolveLocaleForUrlNormalization(
            localeContextFromUrl,
            currentPath,
            currentLocale,
        ).pipe(
            map((input: UrlNormalizationInput): ResolvedLocaleContext => {
                const resolvedLocale: SeoLocale = this.applyRouteLocaleUrlUseCase.execute(
                    cleanUrl,
                    input.locale,
                    input.urlLocaleContext,
                );

                return {
                    currentPath:
                        localeContextFromUrl === null
                            ? toPathOnly(this.location.path())
                            : currentPath,
                    resolvedLocale,
                };
            }),
        );
    }

    /**
     * Sin prefijo en la URL, el locale guardado viaja en urlLocaleContext (el normalizer solo lo respeta
     * asi) y se reescribe la URL. `locale` queda con el fallback de la ruta: si tambien fuera el guardado,
     * el early-return de handle() (target === current) evitaria la reescritura.
     */
    private resolveLocaleForUrlNormalization(
        localeContextFromUrl: string | null,
        currentPath: string,
        fallbackLocale: SeoLocale,
    ): Observable<UrlNormalizationInput> {
        if (localeContextFromUrl !== null) {
            return of({
                locale: this.extractLocaleForUrlNormalization(currentPath, fallbackLocale),
                urlLocaleContext: localeContextFromUrl,
            });
        }

        return this.publicLocaleStorageService.getLocale().pipe(
            map((storedLocale: string | null): UrlNormalizationInput => {
                if (storedLocale === null) {
                    return { locale: fallbackLocale, urlLocaleContext: null };
                }

                const normalizedStoredLocale: SeoLocale = normalizeSeoLocale(storedLocale);

                if (
                    normalizedStoredLocale === SEO_DEFAULT_LOCALE ||
                    !isSeoLocale(normalizedStoredLocale)
                ) {
                    return { locale: fallbackLocale, urlLocaleContext: null };
                }

                return { locale: fallbackLocale, urlLocaleContext: normalizedStoredLocale };
            }),
        );
    }

    public ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    /** Returns the SeoLocale encoded in the URL prefix, or the default locale. */
    private extractLocaleFromPath(pathOnly: string): SeoLocale {
        const localePrefix: string | null = extractLocalePrefixFromPath(pathOnly);

        if (localePrefix === null) {
            return SEO_DEFAULT_LOCALE;
        }

        return normalizeSeoLocale(localePrefix);
    }

    private extractLocaleForUrlNormalization(
        pathOnly: string,
        fallbackLocale: SeoLocale,
    ): SeoLocale {
        const localePrefix: string | null = extractLocalePrefixFromPath(pathOnly);

        if (localePrefix === null) {
            return fallbackLocale;
        }

        const normalizedPrefix: string = normalizeLocaleToken(localePrefix);

        if (isSeoLocale(normalizedPrefix)) {
            return normalizedPrefix;
        }

        return fallbackLocale;
    }
}
